import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/api';

const BroadcastNotificationModal = ({ show, handleClose }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Character limits following iOS/Android best practices
  const TITLE_MAX_LENGTH = 45; // Safe for mobile notification titles
  const BODY_MAX_LENGTH = 120; // Approximately 4 lines of text
  
  // Reset form when the modal closes
  const onHide = () => {
    setTitle('');
    setBody('');
    handleClose();
  };

  // Handle title change with character limit
  const handleTitleChange = (e) => {
    const value = e.target.value;
    if (value.length <= TITLE_MAX_LENGTH) {
      setTitle(value);
    }
  };

  // Handle body change with character limit
  const handleBodyChange = (e) => {
    const value = e.target.value;
    if (value.length <= BODY_MAX_LENGTH) {
      setBody(value);
    }
  };

  // Check if form is valid for sending
  const isFormValid = () => {
    return title.trim().length > 0 && body.trim().length > 0;
  };

  // Check if any field exceeds limits
  const hasExceededLimits = () => {
    return title.length > TITLE_MAX_LENGTH || body.length > BODY_MAX_LENGTH;
  };

  const handleSendBroadcast = async () => {
    // Prevent sending if limits are exceeded
    if (hasExceededLimits()) {
      toast.error('Please ensure title and body are within character limits');
      return;
    }

    // Prevent sending if form is invalid
    if (!isFormValid()) {
      toast.error('Please fill in both title and body');
      return;
    }

    setLoading(true);
    try {
      // Only send title and body now
      const payload = {
        title: title.trim(),
        body: body.trim(),
      };

      // POST /notifications/broadcast
      const response = await api.post('/notifications/broadcast', payload);
      if (response.status === 200) {
        toast.success('Broadcast notification sent successfully!');
        onHide();
      } else {
        toast.error('Failed to send broadcast notification');
      }
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error(error.response?.data?.message || 'Error sending broadcast notification');
    }
    setLoading(false);
  };

  // Get character counter color based on usage
  const getCounterColor = (current, max) => {
    const percentage = (current / max) * 100;
    if (percentage >= 90) return 'text-danger';
    if (percentage >= 75) return 'text-warning';
    return 'text-muted';
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Send Broadcast Notification</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <div className="position-relative">
            <Form.Control
              type="text"
              placeholder="Enter notification title (max 60 characters)"
              value={title}
              onChange={handleTitleChange}
              className={title.length > TITLE_MAX_LENGTH ? 'border-danger' : ''}
            />
            <div className={`position-absolute top-0 end-0 mt-2 me-2 small ${getCounterColor(title.length, TITLE_MAX_LENGTH)}`}>
              {title.length}/{TITLE_MAX_LENGTH}
            </div>
          </div>
          <Form.Text className="text-muted">
            Keep title short and concise for mobile notifications
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Body</Form.Label>
          <div className="position-relative">
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter notification body (max 200 characters)"
              value={body}
              onChange={handleBodyChange}
              className={body.length > BODY_MAX_LENGTH ? 'border-danger' : ''}
            />
            <div className={`position-absolute bottom-0 end-0 mb-2 me-2 small ${getCounterColor(body.length, BODY_MAX_LENGTH)}`}>
              {body.length}/{BODY_MAX_LENGTH}
            </div>
          </div>
          <Form.Text className="text-muted">
            Body can be up to 4 lines for optimal mobile display
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSendBroadcast} 
          disabled={loading || !isFormValid() || hasExceededLimits()}
        >
          {loading ? <Spinner animation="border" size="sm" /> : 'Send'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BroadcastNotificationModal;
